import { ApiManager } from './ApiManager';
import {
  ConversationDTO,
  ConversationMessage,
  ConversationWithMessagesDTO,
  SuggestedAnswerDTO,
} from './models/ConversationDTO';

export type ApiDoctorConversationsGetDoctorConversationsListResult = ConversationDTO[];
export type ApiDoctorConversationsGetDoctorConversationMessageListParams = {
  conversationId: string;
};
export type ApiDoctorConversationsGetDoctorConversationMessageListResult = ConversationWithMessagesDTO;
export type ApiDoctorConversationsPostMessageToDoctorConversationParams = {
  conversationId: string;
  messageText: string;
  repliedToMessageId?: number;
};
export type ApiDoctorConversationsPostMessageToDoctorConversationResult = {
  messageText: string;
  /** The created message, as the conversation shows it. */
  message: ConversationMessage;
};
export type ApiDoctorConversationsGetUnassignedConversationsResult = ConversationDTO[];
export type ApiDoctorConversationsSelfAssignConversationParams = {
  conversationId: string;
};
export type ApiDoctorConversationsSelfAssignConversationResult = void;
export type ApiDoctorConversationsRejectAssignmentParams = {
  conversationId: string;
};
export type ApiDoctorConversationsRejectAssignmentResult = void;
export type ApiDoctorConversationsGetOpenChangeRequestConversationsResult = ConversationDTO[];
export type ApiDoctorConversationsGetWaitingForReassignmentConversationsResult = ConversationDTO[];
export type ApiDoctorConversationsRequestOwnerChangeParams = {
  conversationId: string;
  caregiverGroupId: string;
};
export type ApiDoctorConversationsRequestOwnerChangeResult = void;
export type ApiDoctorConversationsCreateNewConversationWithGroupParams = {
  conversationId: string;
  caregiverGroupId: string;
};
export type ApiDoctorConversationsCreateNewConversationWithGroupResult = void;
export type ApiDoctorConversationsRequestCaregiverAssignmentParams = {
  conversationId: string;
  registeredCaregiverId: string;
};
export type ApiDoctorConversationsRequestCaregiverAssignmentResult = void;
export type ApiDoctorConversationsGetPatientHistoryResult = ConversationDTO[];
export type InvolvedRegisteredCaregiverDTO = {
  id: string;
  display_name: string;
  photo_url: string;
  caregiver_groups: { id: string; name: string }[];
};

export type StatusChangeRequestType = 'CLOSED' | 'ON_HOLD';
export type SuggestedAnswerResponseAction = 'accepted' | 'edited' | 'rejected';

export default class ApiDoctorConversationsManager {
  static async getDoctorConversationsList(): Promise<ApiDoctorConversationsGetDoctorConversationsListResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/doctor-conversations/`);

    return response.data as ApiDoctorConversationsGetDoctorConversationsListResult;
  }

  static async getDoctorConversationMessageList(
    params: ApiDoctorConversationsGetDoctorConversationMessageListParams,
  ): Promise<ApiDoctorConversationsGetDoctorConversationMessageListResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/doctor-conversations/${params.conversationId}/`);

    return response.data as ApiDoctorConversationsGetDoctorConversationMessageListResult;
  }

  static async postMessageToDoctorConversation(
    params: ApiDoctorConversationsPostMessageToDoctorConversationParams,
  ): Promise<ApiDoctorConversationsPostMessageToDoctorConversationResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.post(`/api/doctor-conversations/${params.conversationId}/send/`, {
      message_text: params.messageText,
      ...(params.repliedToMessageId && { replied_to_message_id: params.repliedToMessageId }),
    });

    return {
      messageText: response.data.message_text,
      message: response.data,
    };
  }

  static async getUnassignedConversations(): Promise<ApiDoctorConversationsGetUnassignedConversationsResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/doctor-conversations/unassigned/`);

    return response.data;
  }

  static async selfAssignConversation(
    params: ApiDoctorConversationsSelfAssignConversationParams,
  ): Promise<ApiDoctorConversationsSelfAssignConversationResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(`/api/doctor-conversations/${params.conversationId}/self-assign/`);

    return response.data;
  }

  static async rejectAssignment(
    params: ApiDoctorConversationsRejectAssignmentParams,
  ): Promise<ApiDoctorConversationsRejectAssignmentResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(`/api/doctor-conversations/${params.conversationId}/reject-assignment/`);

    return response.data;
  }

  static async getOpenChangeRequestConversations(): Promise<ApiDoctorConversationsGetOpenChangeRequestConversationsResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/doctor-conversations/requests/`);

    return response.data;
  }

  static async getWaitingForReassignmentConversations(): Promise<ApiDoctorConversationsGetWaitingForReassignmentConversationsResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/doctor-conversations/waiting-for-reassignment/`);

    return response.data;
  }

  static async requestOwnerChange(
    params: ApiDoctorConversationsRequestOwnerChangeParams,
  ): Promise<ApiDoctorConversationsRequestOwnerChangeResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(
      `/api/doctor-conversations/${params.conversationId}/request-owner-change/`,
      {
        caregiver_group: params.caregiverGroupId,
      },
    );

    return response.data;
  }

  static async createNewConversationWithGroup(
    params: ApiDoctorConversationsCreateNewConversationWithGroupParams,
  ): Promise<ApiDoctorConversationsCreateNewConversationWithGroupResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(
      `/api/doctor-conversations/${params.conversationId}/create-new-conversation-with-group/`,
      {
        caregiver_group: params.caregiverGroupId,
      },
    );

    return response.data;
  }

  static async requestCaregiverAssignment(
    params: ApiDoctorConversationsRequestCaregiverAssignmentParams,
  ): Promise<ApiDoctorConversationsRequestCaregiverAssignmentResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(
      `/api/doctor-conversations/${params.conversationId}/request-caregiver-assignment/`,
      {
        registered_caregiver: params.registeredCaregiverId,
      },
    );

    return response.data;
  }

  static async getInvolvedRegisteredCaregivers(conversationId: string): Promise<InvolvedRegisteredCaregiverDTO[]> {
    const apiManager = ApiManager.getInstance();
    const response = await apiManager.get(
      `/api/doctor-conversations/${conversationId}/involved-registered-caregivers/`,
    );
    return response.data as InvolvedRegisteredCaregiverDTO[];
  }

  static async markConversationAsRead(conversationId: string): Promise<void> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(`/api/doctor-conversations/${conversationId}/mark-as-read/`);

    return response.data as void;
  }

  static async requestConversationStatusChange(
    conversationId: string,
    newStatus: StatusChangeRequestType,
  ): Promise<void> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(`/api/doctor-conversations/${conversationId}/update-status/`, {
      new_status: newStatus,
    });

    return response.data as void;
  }

  static async updateConversationSummary(conversationId: string, newSummary: string): Promise<void> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(`/api/doctor-conversations/${conversationId}/update-summary/`, {
      summary: newSummary,
    });

    return response.data as void;
  }

  static async getPatientConversationHistory(
    patientId: string,
  ): Promise<ApiDoctorConversationsGetPatientHistoryResult> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/doctor-conversations/patient-history/${patientId}/`);

    return response.data;
  }

  static async getSuggestedAnswer(conversationId: string): Promise<SuggestedAnswerDTO | null> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/doctor-conversations/${conversationId}/suggested-answer/`);

    if (response.status === 204) {
      return null;
    }

    return response.data;
  }

  static async markSuggestedAnswerAsSeen(conversationId: string, suggestedAnswerId: number): Promise<void> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(
      `/api/doctor-conversations/${conversationId}/suggested-answer/${suggestedAnswerId}/mark-as-seen/`,
    );

    return response.data as void;
  }

  static async respondToSuggestedAnswer(
    conversationId: string,
    suggestedAnswerId: number,
    action: SuggestedAnswerResponseAction,
  ): Promise<void> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.patch(
      `/api/doctor-conversations/${conversationId}/suggested-answer/${suggestedAnswerId}/respond/`,
      { action },
    );

    return response.data as void;
  }

  static async editMessage(conversationId: string, messageId: number, messageText: string): Promise<void> {
    const apiManager = ApiManager.getInstance();

    await apiManager.patch(`/api/doctor-conversations/${conversationId}/messages/${messageId}/edit/`, {
      message_text: messageText,
    });
  }

  static async deleteMessage(conversationId: string, messageId: number): Promise<void> {
    const apiManager = ApiManager.getInstance();

    await apiManager.patch(`/api/doctor-conversations/${conversationId}/messages/${messageId}/delete/`);
  }
}
